import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../config';

const AuthContext = createContext(null);

// Tiempo de aviso antes de expirar (5 minutos en ms)
const WARNING_TIME_BEFORE_EXPIRY = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [token, setTokenState] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const saved = localStorage.getItem('selectedCompany');
    return saved ? JSON.parse(saved) : null;
  });
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(null);

  const warningTimerRef = useRef(null);
  const expiryTimerRef = useRef(null);
  const countdownRef = useRef(null);

  // Limpiar todos los timers
  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  // Logout - limpiar todo
  const logout = useCallback(() => {
    clearAllTimers();
    localStorage.removeItem('token');
    localStorage.removeItem('selectedCompany');
    setTokenState(null);
    setUser(null);
    setSelectedCompany(null);
    setShowExpiryWarning(false);
    setTimeUntilExpiry(null);
  }, [clearAllTimers]);

  // Configurar timers basados en la expiración del token
  const setupTimers = useCallback((tokenValue) => {
    clearAllTimers();

    try {
      const decoded = jwtDecode(tokenValue);
      const expiryTime = decoded.exp * 1000; // convertir a ms
      const now = Date.now();
      const timeUntilExpiry = expiryTime - now;

      if (timeUntilExpiry <= 0) {
        // Token ya expiró
        logout();
        return;
      }

      // Timer para mostrar aviso
      const warningTime = timeUntilExpiry - WARNING_TIME_BEFORE_EXPIRY;
      if (warningTime > 0) {
        warningTimerRef.current = setTimeout(() => {
          setShowExpiryWarning(true);
          // Iniciar countdown
          let remaining = WARNING_TIME_BEFORE_EXPIRY;
          setTimeUntilExpiry(Math.floor(remaining / 1000));
          countdownRef.current = setInterval(() => {
            remaining -= 1000;
            setTimeUntilExpiry(Math.floor(remaining / 1000));
            if (remaining <= 0) {
              clearInterval(countdownRef.current);
            }
          }, 1000);
        }, warningTime);
      } else {
        // Ya estamos dentro del período de aviso
        setShowExpiryWarning(true);
        let remaining = timeUntilExpiry;
        setTimeUntilExpiry(Math.floor(remaining / 1000));
        countdownRef.current = setInterval(() => {
          remaining -= 1000;
          setTimeUntilExpiry(Math.floor(remaining / 1000));
          if (remaining <= 0) {
            clearInterval(countdownRef.current);
          }
        }, 1000);
      }

      // Timer para expiración
      expiryTimerRef.current = setTimeout(() => {
        setShowExpiryWarning(false);
        logout();
        // Redirigir al login
        window.location.href = '/login';
      }, timeUntilExpiry);

      // Extraer datos del usuario
      setUser({
        id: decoded.id,
        email: decoded.email,
        type: decoded.type
      });

    } catch (error) {
      console.error('Error decodificando token:', error);
      logout();
    }
  }, [clearAllTimers, logout]);

  // Setear token
  const setToken = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
      setTokenState(newToken);
      setupTimers(newToken);
    } else {
      logout();
    }
  }, [setupTimers, logout]);

  // Renovar token
  const refreshToken = useCallback(async () => {
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        setShowExpiryWarning(false);
        setTimeUntilExpiry(null);
        return true;
      } else {
        const errorData = await response.json();
        if (errorData.expired || response.status === 401) {
          logout();
          window.location.href = '/login';
        }
        return false;
      }
    } catch (error) {
      console.error('Error renovando token:', error);
      return false;
    }
  }, [token, setToken, logout]);

  // Actualizar empresa seleccionada
  const setCompany = useCallback((company) => {
    if (company) {
      localStorage.setItem('selectedCompany', JSON.stringify(company));
    } else {
      localStorage.removeItem('selectedCompany');
    }
    setSelectedCompany(company);
  }, []);

  // Cerrar aviso sin renovar (el usuario decidió no continuar)
  const dismissWarning = useCallback(() => {
    setShowExpiryWarning(false);
  }, []);

  // Inicializar al cargar
  useEffect(() => {
    if (token) {
      setupTimers(token);
    }
    return () => clearAllTimers();
  }, []); // Solo al montar

  const value = {
    token,
    user,
    selectedCompany,
    showExpiryWarning,
    timeUntilExpiry,
    setToken,
    setCompany,
    logout,
    refreshToken,
    dismissWarning,
    isAuthenticated: !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
